import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { Crown, Flame, Sparkles, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  getAiQueueUi,
  publishAiQueueUi,
  subscribeAiQueueUi,
  type AiQueueUiState,
} from "@/lib/aiQueueUi";

const SPOTLIGHT_MESSAGES = [
  "aiQueue.spotlight1",
  "aiQueue.spotlight2",
  "aiQueue.spotlight3",
  "aiQueue.spotlight4",
] as const;

function ticketsForPosition(position: number) {
  const ahead = Math.max(0, position - 1);
  const shownAhead = Math.min(ahead, 5);
  const hiddenAhead = ahead - shownAhead;
  return { shownAhead, hiddenAhead };
}

export function AiQueueOverlay() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [state, setState] = useState<AiQueueUiState | null>(getAiQueueUi);
  const [messageIndex, setMessageIndex] = useState(0);
  const isOverlayRoute = location.pathname.startsWith("/overlay/");

  useEffect(() => subscribeAiQueueUi(setState), []);

  useEffect(() => {
    if (!state) return;
    const id = window.setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % SPOTLIGHT_MESSAGES.length);
    }, 2400);
    return () => window.clearInterval(id);
  }, [state]);

  useEffect(() => {
    if (!state) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [state]);

  const { shownAhead, hiddenAhead } = useMemo(
    () => ticketsForPosition(state?.position ?? 1),
    [state?.position],
  );

  if (!state || isOverlayRoute) return null;

  const timedOut = state.status === "timeout";
  const upgradeUrl = state.upgradeUrl || "/pricing";

  const skipLine = () => {
    publishAiQueueUi(null);
    navigate(upgradeUrl);
  };

  return (
    <div
      className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center p-3 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ai-queue-title"
      aria-live="polite"
    >
      {timedOut ? (
        <button
          type="button"
          className="absolute inset-0 bg-stone-950/80 backdrop-blur-md"
          aria-label={t("common.close", "Close")}
          onClick={() => publishAiQueueUi(null)}
        />
      ) : (
        <div className="absolute inset-0 bg-stone-950/80 backdrop-blur-md" />
      )}

      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-24 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full bg-amber-500/25 blur-3xl animate-pulse" />
        <div className="absolute bottom-0 left-8 h-48 w-48 rounded-full bg-orange-600/20 blur-3xl ai-queue-steam" />
        <div className="absolute bottom-10 right-10 h-40 w-40 rounded-full bg-yellow-400/10 blur-3xl ai-queue-steam-delayed" />
        {Array.from({ length: 8 }).map((_, i) => (
          <span
            key={i}
            className="absolute bottom-0 text-2xl opacity-40 ai-queue-bubble"
            style={{
              left: `${8 + i * 12}%`,
              animationDelay: `${i * 0.45}s`,
            }}
          >
            {i % 2 === 0 ? "🔥" : "💨"}
          </span>
        ))}
      </div>

      <div className="relative z-10 w-full max-w-lg overflow-hidden rounded-3xl border border-amber-300/30 bg-gradient-to-b from-stone-900 via-stone-950 to-black shadow-[0_0_80px_rgba(251,146,60,0.35)]">
        <div className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-amber-600 via-yellow-300 to-orange-500" />
        <div className="pointer-events-none absolute inset-x-6 top-8 h-px bg-amber-200/20 [background-image:radial-gradient(circle,rgba(251,191,36,0.55)_1px,transparent_1.5px)] [background-size:10px_2px]" />

        {timedOut && (
          <button
            type="button"
            onClick={() => publishAiQueueUi(null)}
            className="absolute right-3 top-3 z-20 rounded-full p-2 text-amber-100/70 hover:bg-white/10 hover:text-white min-h-[44px] min-w-[44px]"
            aria-label={t("common.close", "Close")}
          >
            <X className="h-5 w-5" />
          </button>
        )}

        <div className="px-5 pt-8 pb-6 sm:px-8 sm:pt-10 sm:pb-8 text-center">
          <p className="mb-3 inline-flex items-center gap-2 rounded-full border border-amber-400/30 bg-amber-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-amber-200">
            <Flame className="h-3.5 w-3.5 animate-pulse" />
            {t("aiQueue.passTitle", "Kitchen pass")}
          </p>

          <h2 id="ai-queue-title" className="font-heading text-2xl sm:text-3xl font-bold text-amber-50">
            {timedOut
              ? t("aiQueue.timeoutTitle", "Still packed")
              : t("aiQueue.title", "You're in line")}
          </h2>
          <p
            key={timedOut ? "timeout" : messageIndex}
            className="mt-2 text-sm sm:text-base text-amber-100/75 min-h-[1.5rem] animate-fade-in"
          >
            {timedOut
              ? t("aiQueue.timeout")
              : t(SPOTLIGHT_MESSAGES[messageIndex], {
                  defaultValue: "The pass is packed — a chef will grab your ticket soon.",
                })}
          </p>

          <div className="relative mx-auto mt-6 max-w-sm">
            <div className="rounded-2xl border-2 border-dashed border-amber-400/40 bg-[linear-gradient(180deg,rgba(251,191,36,0.16),rgba(28,25,23,0.9))] p-5 shadow-inner">
              <p className="text-[10px] font-bold uppercase tracking-[0.35em] text-amber-200/80">
                {t("aiQueue.ticketLabel", "Your ticket")}
              </p>
              <p className="mt-1 font-heading text-6xl sm:text-7xl font-black leading-none text-amber-300 drop-shadow-[0_0_18px_rgba(251,191,36,0.65)] tabular-nums">
                #{state.position}
              </p>
              <p className="mt-2 text-xs uppercase tracking-widest text-amber-100/60">
                {t("aiQueue.nowHolding", "Holding for the next burner")}
              </p>
            </div>
            <Sparkles className="absolute -right-1 -top-1 h-6 w-6 text-yellow-300 ai-queue-sparkle" />
            <Sparkles className="absolute -left-2 bottom-3 h-4 w-4 text-amber-200 ai-queue-sparkle-delayed" />
          </div>

          <div className="mt-7 flex items-end justify-center gap-2 sm:gap-3" aria-hidden>
            {hiddenAhead > 0 && (
              <div className="mb-2 rounded-md border border-amber-500/20 bg-stone-800/70 px-2 py-3 text-[10px] text-amber-200/70">
                +{hiddenAhead}
              </div>
            )}
            {Array.from({ length: shownAhead }).map((_, i) => (
              <div
                key={`ahead-${i}`}
                className="ai-queue-ticket flex h-[4.75rem] w-12 sm:w-14 flex-col items-center justify-center rounded-md border border-dashed border-amber-900/70 bg-stone-800/80 text-amber-100/50"
                style={{ animationDelay: `${i * 0.12}s` }}
              >
                <span className="text-lg">👨‍🍳</span>
                <span className="text-[9px] font-mono">#{i + 1 + hiddenAhead}</span>
              </div>
            ))}
            <div className="ai-queue-you relative flex h-[5.5rem] w-16 sm:w-[4.5rem] flex-col items-center justify-center rounded-md border-2 border-amber-200 bg-gradient-to-b from-amber-300 to-orange-400 text-stone-900 shadow-[0_0_28px_rgba(251,191,36,0.55)]">
              <span className="absolute -top-2 rounded-full bg-stone-950 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider text-amber-300">
                {t("aiQueue.you", "You")}
              </span>
              <span className="text-2xl">🎫</span>
              <span className="font-mono text-xs font-black">#{state.position}</span>
            </div>
          </div>

          <div className="mx-auto mt-6 h-1.5 w-48 overflow-hidden rounded-full bg-amber-950">
            <div className="h-full w-1/3 rounded-full bg-gradient-to-r from-amber-500 via-yellow-300 to-orange-400 ai-queue-bar" />
          </div>

          <div className="mt-7 flex flex-col gap-2">
            <Button
              size="lg"
              onClick={skipLine}
              className="min-h-[48px] w-full gap-2 bg-gradient-to-r from-amber-400 via-yellow-300 to-orange-400 text-stone-900 hover:from-amber-300 hover:to-orange-300 font-bold shadow-[0_8px_30px_rgba(251,146,60,0.35)]"
            >
              <Crown className="h-4 w-4" />
              {t("aiQueue.upgrade")}
            </Button>
            <p className="text-xs text-amber-100/55">
              {t("aiQueue.upgradeHint", "Pro Chef and Chef Master skip this line.")}
            </p>
            {timedOut && (
              <Button
                variant="ghost"
                onClick={() => publishAiQueueUi(null)}
                className="min-h-[44px] text-amber-100/80 hover:text-white"
              >
                {t("common.close", "Close")}
              </Button>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes ai-queue-steam {
          0%, 100% { transform: translateY(0) scale(1); opacity: 0.35; }
          50% { transform: translateY(-18px) scale(1.08); opacity: 0.6; }
        }
        @keyframes ai-queue-bubble {
          0% { transform: translateY(0); opacity: 0; }
          20% { opacity: 0.45; }
          100% { transform: translateY(-70vh); opacity: 0; }
        }
        @keyframes ai-queue-ticket {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }
        @keyframes ai-queue-you {
          0%, 100% { transform: translateY(0) rotate(-2deg); }
          50% { transform: translateY(-8px) rotate(2deg); }
        }
        @keyframes ai-queue-bar {
          0% { transform: translateX(-120%); }
          100% { transform: translateX(360%); }
        }
        @keyframes ai-queue-sparkle {
          0%, 100% { opacity: 0.35; transform: scale(0.85) rotate(0deg); }
          50% { opacity: 1; transform: scale(1.15) rotate(18deg); }
        }
        .ai-queue-steam { animation: ai-queue-steam 3.2s ease-in-out infinite; }
        .ai-queue-steam-delayed { animation: ai-queue-steam 4s ease-in-out infinite 0.8s; }
        .ai-queue-bubble { animation: ai-queue-bubble 5.5s linear infinite; }
        .ai-queue-ticket { animation: ai-queue-ticket 1.8s ease-in-out infinite; }
        .ai-queue-you { animation: ai-queue-you 1.4s ease-in-out infinite; }
        .ai-queue-bar { animation: ai-queue-bar 1.6s linear infinite; }
        .ai-queue-sparkle { animation: ai-queue-sparkle 1.5s ease-in-out infinite; }
        .ai-queue-sparkle-delayed { animation: ai-queue-sparkle 1.8s ease-in-out infinite 0.4s; }
        @media (prefers-reduced-motion: reduce) {
          .ai-queue-steam, .ai-queue-steam-delayed, .ai-queue-bubble,
          .ai-queue-ticket, .ai-queue-you, .ai-queue-bar,
          .ai-queue-sparkle, .ai-queue-sparkle-delayed {
            animation: none !important;
          }
        }
      `}</style>
    </div>
  );
}
